import { createStore } from "zustand/vanilla";
import { subscribeWithSelector } from "zustand/middleware";
import { modules, type ModulesSlices, type ModuleKey } from "../modules";
import type { CompanyFilterStore } from "./types";
import { composeCompanyFilters } from "./composer";

function getDefaultSlices(): ModulesSlices {
  // Type safety: Building ModulesSlices dynamically from modules. Each key K will have
  // modules[K].defaultSlice assigned, matching the ModulesSlices mapped type definition.
  const defaults: any = {};
  (Object.keys(modules) as ModuleKey[]).forEach((key) => {
    defaults[key] = modules[key].defaultSlice;
  });
  return defaults as ModulesSlices;
}

export function createCompanyFilterStore() {
  return createStore<CompanyFilterStore>()(
    subscribeWithSelector((set, get) => {
      const defaultSlices = getDefaultSlices();
      return {
        slices: defaultSlices,
        draftFilters: undefined,
        appliedFilters: undefined,
        persistedFilters: undefined,

        setSlice: (key, sliceOrUpdater) => {
          set((state) => {
            const currentSlice = state.slices[key];
            const newSlice =
              typeof sliceOrUpdater === "function"
                ? sliceOrUpdater(currentSlice)
                : sliceOrUpdater;

            const newSlices = {
              ...state.slices,
              [key]: newSlice,
            } as ModulesSlices;
            const draftFilters = composeCompanyFilters(newSlices);
            return { slices: newSlices, draftFilters };
          });
        },

        resetAll: () => {
          const newSlices = getDefaultSlices();
          set({
            slices: newSlices,
            draftFilters: undefined,
            appliedFilters: undefined,
            persistedFilters: undefined,
          });
        },

        _applyDraftToApplied: () => {
          set((state) => ({ appliedFilters: state.draftFilters }));
        },

        applyDraft: () => {
          set((state) => ({ appliedFilters: state.draftFilters }));
        },

        revertDraftToApplied: () => {
          set((state) => {
            const applied = state.appliedFilters;
            if (!applied) {
              return {
                slices: getDefaultSlices(),
                draftFilters: undefined,
              };
            }

            // Rebuild slices from the applied filters so the editor UI reflects
            // the last confirmed state. Mirrors hydrate, but leaves
            // appliedFilters/persistedFilters untouched.
            const slices: any = {};
            (Object.keys(modules) as ModuleKey[]).forEach((key) => {
              slices[key] = modules[key].fromCompanyFilters(applied);
            });

            return {
              slices: slices as ModulesSlices,
              draftFilters: composeCompanyFilters(slices as ModulesSlices),
            };
          });
        },

        hydrateFromCompanyFilters: (filters) => {
          if (!filters) {
            const newSlices = getDefaultSlices();
            set({
              slices: newSlices,
              draftFilters: undefined,
              appliedFilters: undefined,
              persistedFilters: undefined,
            });
            return;
          }

          // Type safety: Building ModulesSlices dynamically from API filters. Each key K will have
          // modules[K].fromCompanyFilters(filters) assigned, which returns modules[K].defaultSlice type.
          const slices: any = {};
          (Object.keys(modules) as ModuleKey[]).forEach((key) => {
            slices[key] = modules[key].fromCompanyFilters(filters);
          });

          const draftFilters = composeCompanyFilters(slices as ModulesSlices);
          set({
            slices: slices as ModulesSlices,
            draftFilters,
            appliedFilters: draftFilters,
            persistedFilters: draftFilters,
          });
        },

        serializeAppliedToJson: () => {
          const { appliedFilters } = get();
          return appliedFilters ? JSON.stringify(appliedFilters) : "{}";
        },

        markAsPersisted: () => {
          set((state) => ({ persistedFilters: state.appliedFilters }));
        },

        clearDraft: () => {
          // Reset only the editable draft state. Applied/persisted filters are
          // left untouched so the change still requires an explicit confirm
          // (manual confirm flow).
          set({
            slices: getDefaultSlices(),
            draftFilters: undefined,
          });
        },

        clearAll: () => {
          set({
            slices: getDefaultSlices(),
            draftFilters: undefined,
            appliedFilters: undefined,
            persistedFilters: undefined,
          });
        },
      };
    })
  );
}
